import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  IClinicalHistoryStructureDetail,
  IClinicalHistoryStructureSummary,
  ICreateClinicalHistoryStructureItemPayload,
  ICreateClinicalHistoryStructurePayload,
} from '../../_interfaces/IClinicalHistoryStructure';
import {
  IClinicalHistoryTemplate,
  ICreateClinicalHistoryTemplatePayload,
} from '../../_interfaces/IClinicalHistory';

@Injectable({
  providedIn: 'root',
})
export class ClinicalHistoryStructuresService {
  private readonly baseUrl = `${environment.api.protocol}://${environment.api.host}:${environment.api.port}/api/v1`;

  constructor(private readonly http: HttpClient) {}

  private scope(customersId: number): string {
    return `${this.baseUrl}/customers/${customersId}/clinical-history-structures`;
  }

  private templatesScope(customersId: number): string {
    return `${this.scope(customersId)}/templates`;
  }

  list(
    customersId: number,
  ): Observable<IClinicalHistoryStructureSummary[]> {
    return this.http.get<IClinicalHistoryStructureSummary[]>(
      this.scope(customersId),
    );
  }

  get(
    customersId: number,
    structureId: number,
  ): Observable<IClinicalHistoryStructureDetail> {
    return this.http.get<IClinicalHistoryStructureDetail>(
      `${this.scope(customersId)}/${structureId}`,
    );
  }

  create(
    customersId: number,
    payload: ICreateClinicalHistoryStructurePayload,
  ): Observable<IClinicalHistoryStructureDetail> {
    return this.http.post<IClinicalHistoryStructureDetail>(
      this.scope(customersId),
      payload,
    );
  }

  update(
    customersId: number,
    structureId: number,
    payload: Partial<ICreateClinicalHistoryStructurePayload>,
  ): Observable<IClinicalHistoryStructureDetail> {
    return this.http.patch<IClinicalHistoryStructureDetail>(
      `${this.scope(customersId)}/${structureId}`,
      payload,
    );
  }

  delete(customersId: number, structureId: number) {
    return this.http.delete<{ success: boolean }>(
      `${this.scope(customersId)}/${structureId}`,
    );
  }

  listTemplates(customersId: number): Observable<IClinicalHistoryTemplate[]> {
    return this.http.get<IClinicalHistoryTemplate[]>(this.templatesScope(customersId));
  }

  createTemplate(
    customersId: number,
    payload: ICreateClinicalHistoryTemplatePayload,
  ): Observable<IClinicalHistoryTemplate> {
    return this.http.post<IClinicalHistoryTemplate>(
      this.templatesScope(customersId),
      payload,
    );
  }

  updateTemplate(
    customersId: number,
    templateId: number,
    payload: Partial<ICreateClinicalHistoryTemplatePayload>,
  ): Observable<IClinicalHistoryTemplate> {
    return this.http.patch<IClinicalHistoryTemplate>(
      `${this.templatesScope(customersId)}/${templateId}`,
      payload,
    );
  }

  deleteTemplate(
    customersId: number,
    templateId: number,
  ): Observable<{ success: boolean }> {
    return this.http.delete<{ success: boolean }>(
      `${this.templatesScope(customersId)}/${templateId}`,
    );
  }

  createItem(
    customersId: number,
    structureId: number,
    payload: ICreateClinicalHistoryStructureItemPayload,
  ): Observable<IClinicalHistoryStructureDetail> {
    return this.http.post<IClinicalHistoryStructureDetail>(
      `${this.scope(customersId)}/${structureId}/items`,
      payload,
    );
  }

  updateItem(
    customersId: number,
    structureId: number,
    itemId: number,
    payload: Partial<ICreateClinicalHistoryStructureItemPayload>,
  ): Observable<IClinicalHistoryStructureDetail> {
    return this.http.patch<IClinicalHistoryStructureDetail>(
      `${this.scope(customersId)}/${structureId}/items/${itemId}`,
      payload,
    );
  }

  deleteItem(
    customersId: number,
    structureId: number,
    itemId: number,
  ): Observable<IClinicalHistoryStructureDetail> {
    return this.http.delete<IClinicalHistoryStructureDetail>(
      `${this.scope(customersId)}/${structureId}/items/${itemId}`,
    );
  }
}
